import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAngularModule } from '../../../modules/mat-angular.module';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TemplateCommonService, AnalysisResponse, TabFlow } from '../services/template-common.service';

// Import existing tab components
import { BrandAnalysisTabComponent } from '../../../tabs/brand-analysis-tab/brand-analysis-tab.component';
import { SentimentAnalysisTabComponent } from '../../../tabs/sentiment-analysis-tab/sentiment-analysis-tab.component';
import { CategoryAnalysisTabComponent } from '../../../tabs/category-analysis-tab/category-analysis-tab.component';
import { AreaChartTabComponent } from '../../../tabs/area-chart-tab/area-chart-tab.component';
import { ContentAnalysisTabComponent } from '../../../tabs/content-analysis-tab/content-analysis-tab.component';
import { ContentCreationTabComponent } from '../../../tabs/content-creation-tab/content-creation-tab.component';
import { MethodologyTabComponent } from '../../../tabs/methodology-tab/methodology-tab.component';
import { AIVisibilityComponent } from '../../ai-visibility/ai-visibility.component';
import { AICitationsComponent } from '../../ai-citations/ai-citations.component';
import { ContentCreationComponent } from '../../content-creation/content-creation.component';
import { StructuredResponseComponent } from '../../structured-response/structured-response.component';
import { SourceChipsComponent, SourceChip } from '../../source-chips/source-chips.component';
import { TableComponent, TableColumn } from '../../table/table.component';
import { CitationBadgeComponent, CitationBadge } from '../../citation-badge/citation-badge.component';
import { sampleEducationData } from '../../../sample-education';
import { CommonRoiComparisionComponent } from '../common-roi-comparision/common-roi-comparision.component';
import { AiContentIndexComponent } from '../../ai-content-index/ai-content-index.component';
import { CommonConsumerInsightsComponent } from '../common-consumer-insights/common-consumer-insights.component';
import { CommonPerformanceTrackingComponent } from '../common-performance-tracking/common-performance-tracking.component';
import { CommonMethodologyComponent } from '../common-methodology/common-methodology.component';
import { EducationOverviewComponent } from '../../education-overview/education-overview.component';
import { EvidenceAndRecommendationsComponent } from '../../evidence-and-recommendations/evidence-and-recommendations.component';

export interface TabConfig {
  id: string;
  name: string;
  icon: string;
  component: string;
  showForRole?: string;
  showForPlan?: string;
  dataKeys?: string[];
  visualizationTypes?: string[];
  reloadable?: boolean;
  enhanced?: boolean;
}

export interface TabsConfiguration {
  entityType: 'university' | 'brand';
  entityLabel: string;
  entityPlural: string;
  defaultTab: string;
  tabs: TabConfig[];
  tableColumns?: TableColumn[];
  displayedColumns?: string[];
  chartMetrics?: string[];
  maxEntities?: number;
}

export interface TabData {
  [key: string]: any;
}

export interface TabState {
  activeTab: string | null;
  isLoading: boolean;
  error: string | null;
  data: TabData | null;
  evidenceSources: any[];
  recommendations: any[];
  citations: any[];
  sourceChips: SourceChip[];
  citationBadge: CitationBadge | null;
}

@Component({
  selector: 'app-common-tabs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAngularModule,
    MatProgressBarModule,
    MatCardModule,
    MatTableModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatMenuModule,
    MatMenuModule,
    // Tab Components
    // BrandAnalysisTabComponent, // Removed as it's no longer used
    SentimentAnalysisTabComponent,
    CategoryAnalysisTabComponent,
    AiContentIndexComponent,
    CommonConsumerInsightsComponent,
    CommonPerformanceTrackingComponent,
    CommonMethodologyComponent,
    // AreaChartTabComponent,
    // ContentAnalysisTabComponent, // Removed as it's no longer used for key-influence-sources
    // ContentCreationTabComponent,
    // MethodologyTabComponent, // Removed as it's replaced by CommonMethodologyComponent
    AIVisibilityComponent,
    AICitationsComponent,
    ContentCreationComponent,
    // StructuredResponseComponent,
    // SourceChipsComponent,
    CommonRoiComparisionComponent,
    EducationOverviewComponent,
    EvidenceAndRecommendationsComponent,
  ],
  templateUrl: './common-tabs.component.html',
  styleUrl: './common-tabs.component.scss'
})
export class CommonTabsComponent implements OnInit, OnDestroy {
  @Input() config!: TabsConfiguration;
  @Input() role: string = 'student';
  @Input() plan: string = 'free';
  @Input() selectedEntities: any[] = [];
  @Input() category: string = 'MBA';
  @Input() country: string = 'UK';

  @Output() tabChange = new EventEmitter<string>();
  @Output() reloadTab = new EventEmitter<string>();
  @Output() visualizationChange = new EventEmitter<{ tabId: string, type: string }>();

  // Tab state
  activeTab: string | null = null;
  isLoading = false;
  isTabLoading = false; // Separate loading state for tabs
  error: string | null = null;
  tabData: TabData | null | any = null;

  // Tab-specific data storage to prevent data loss when switching tabs
  private tabDataCache: { [tabId: string]: any } = {};

  // Service subscriptions
  private serviceSubscriptions: Subscription[] = [];
  private currentTabFlow: TabFlow = { type: 'education', activeTab: 'overview' };

  // Enhanced data
  evidenceSources: any[] = [];
  recommendations: any[] = [];
  citations: any[] = [];
  sourceChips: SourceChip[] = [];
  citationBadge: CitationBadge | null = null;

  // Visualization state
  visualizationType: 'radar' | 'bar' | 'cards' = 'cards';
  studentComparisonVisualizationType: 'bar' | 'cards' = 'cards';

  // Data observables for sharing between components
  private dataSubject = new BehaviorSubject<TabData | null>(null);
  public data$: Observable<TabData | null> = this.dataSubject.asObservable();

  private evidenceSubject = new BehaviorSubject<any[]>([]);
  public evidence$: Observable<any[]> = this.evidenceSubject.asObservable();

  private recommendationsSubject = new BehaviorSubject<any[]>([]);
  public recommendations$: Observable<any[]> = this.recommendationsSubject.asObservable();

  private destroy$ = new Subject<void>();

  constructor(
    private templateCommonService: TemplateCommonService
  ) { }

  ngOnInit(): void {
    this.initializeTabs();
    this.setupDataObservables();
    this.subscribeToServiceObservables();
    this.openTab();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Unsubscribe from service subscriptions
    this.serviceSubscriptions.forEach(sub => sub.unsubscribe());
  }

  private initializeTabs(): void {
    if (this.config && this.config.defaultTab) {
      this.activeTab = this.config.defaultTab;
    }
  }


  private setupDataObservables(): void {
    // Update observables when data changes
    this.data$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.tabData = data;
    });
  }

  private subscribeToServiceObservables(): void {
    // Subscribe to analysis response from service
    const analysisSub = this.templateCommonService.analysisResponse$.subscribe(response => {
      if (response) {
        this.handleAnalysisResponse(response);
      }
    });

    // Subscribe to loading state
    const loadingSub = this.templateCommonService.isLoading$.subscribe(loading => {
      this.isLoading = loading;
    });

    // Subscribe to error state
    const errorSub = this.templateCommonService.error$.subscribe(error => {
      this.error = error;
    });

    // Subscribe to tab flow
    const tabFlowSub = this.templateCommonService.tabFlow$.subscribe(tabFlow => {
      this.currentTabFlow = tabFlow;
      // Update active tab if it's different
      if (tabFlow.activeTab !== this.activeTab) {
        this.activeTab = tabFlow.activeTab;
      }
    });

    // Add subscriptions to array
    this.serviceSubscriptions.push(analysisSub, loadingSub, errorSub, tabFlowSub);
  }

  private handleAnalysisResponse(response: AnalysisResponse): void {
    // // console.log('CommonTabsComponent: Handling analysis response:', response);

    // Check if we're in education flow
    if (this.currentTabFlow.type === 'education' || this.currentTabFlow.type === 'education-university') {
      // For overview tab, extract chart, table and citations
      if (this.activeTab === 'overview') {
        const overviewData = {
          table: response.table || [],
          chart: response.chart || null,
          evidence_sources: response.evidence_sources || [],
          recommendations: [],
          citations: response.evidence_sources || [],
          source_chips: [],
          citation_badge: null
        };

        this.updateTabData(overviewData, 'overview');
        // // console.log('CommonTabsComponent: Updated overview tab with data:', overviewData);
      }
    }
  }

  getVisibleTabs(): TabConfig[] {
    if (!this.config || !this.config.tabs) return [];

    return this.config.tabs.filter(tab => {
      // Check role restrictions
      if (tab.showForRole && tab.showForRole !== this.role) {
        return false;
      }

      // Check plan restrictions
      if (tab.showForPlan && tab.showForPlan !== this.plan) {
        return false;
      }

      return true;
    });
  }

  openTab(tabId: string = 'overview'): void {
    // // console.log('Active tab:', tabId);
    this.activeTab = tabId;
    this.tabChange.emit(tabId);

    // Update tab flow in service
    this.currentTabFlow.activeTab = tabId;
    this.templateCommonService.updateTabFlow(this.currentTabFlow);

    this.loadTabData(tabId);
  }

  reloadCurrentTab(): void {
    // // console.log("this.activeTab", this.activeTab);
    if (this.activeTab) {
      // // console.log('Reloading current tab:', this.activeTab);

      // For overview tab, only emit the reload event - the parent will handle the API call
      if (this.activeTab === 'overview') {
        this.reloadTab.emit(this.activeTab);
      } else {
        // For other tabs, use the existing loadTabData method
        this.reloadTab.emit(this.activeTab);
        this.loadTabData(this.activeTab, true);
      }
    }
  }

  switchVisualization(type: 'radar' | 'bar' | 'cards'): void {
    this.visualizationType = type;
    if (this.activeTab) {
      this.visualizationChange.emit({ tabId: this.activeTab, type });
    }
  }

  switchStudentComparisonVisualization(type: 'bar' | 'cards'): void {
    this.studentComparisonVisualizationType = type;
    if (this.activeTab) {
      this.visualizationChange.emit({ tabId: this.activeTab, type });
    }
  }

  private loadTabData(tabId: string, forceReload: boolean = false): void {
    // // console.log('CommonTabsComponent: Loading tab data for:', tabId, 'Flow:', this.currentTabFlow.type);

    this.isTabLoading = true;
    this.error = null;

    // Check if we have filter data and compare state
    const currentFilterData = this.templateCommonService.getCurrentFilterData();
    const compareClicked = this.templateCommonService.getCompareClicked();
    const hasFilterData = currentFilterData &&
      currentFilterData.selectedEntities &&
      currentFilterData.selectedEntities.length > 0 &&
      currentFilterData.category &&
      currentFilterData.country;

    // // console.log('Filter data check:', { hasFilterData, currentFilterData, compareClicked, forceReload });

    // Check flow type and handle accordingly
    if (this.currentTabFlow.type === 'education' || this.currentTabFlow.type === 'education-university') {
      // Show API data only if COMPARE button was clicked AND we have filter data
      // OR if it's a force reload (refresh button) with filter data
      if (forceReload || (compareClicked && hasFilterData) || (forceReload && hasFilterData)) {
        // // console.log('Loading API data - compare clicked or force reload with filter data');
        this.handleEducationTabLoadWithAPI(tabId, forceReload);
      } else {
        // Default behavior: show sample data
        // // console.log('Loading sample data - default behavior');
        this.handleEducationTabLoadWithSampleData(tabId);
      }
    } else {
      // For other flows, use sample data for now
      const sampleData = this.getSampleDataForTab(tabId);
      this.updateTabData(sampleData);
      this.isTabLoading = false;
    }
  }

  private handleEducationTabLoadWithAPI(tabId: string, forceReload: boolean = false): void {
    // // console.log('Loading tab data with API for:', tabId);

    // Handle AI Visibility Index tab first
    if (tabId === 'ai-visibility-index') {
      const cachedData = this.tabDataCache['ai-visibility-index'];
      if (!forceReload && cachedData?.ai_visibility_data) {
        // // console.log('AI Visibility Index data found in cache, loading from cache');
        this.updateTabData(cachedData, 'ai-visibility-index');
        this.isTabLoading = false;
        return;
      }
      // Only load API data if we have filter data and compare is clicked or force reload
      const currentFilterData = this.templateCommonService.getCurrentFilterData();
      const compareClicked = this.templateCommonService.getCompareClicked();
      const hasFilterData = currentFilterData &&
        currentFilterData.selectedEntities &&
        currentFilterData.selectedEntities.length > 0 &&
        currentFilterData.category &&
        currentFilterData.country;

      if (forceReload || (compareClicked && hasFilterData) || (forceReload && hasFilterData)) {
        this.loadAIVisibilityIndexData(forceReload);
      } else {
        // Load sample data
        this.handleEducationTabLoadWithSampleData(tabId);
      }
      return;
    }

    // Handle Category Analysis tab specifically
    if (tabId === 'category-analysis') {
      const currentFilterData = this.templateCommonService.getCurrentFilterData();
      const compareClicked = this.templateCommonService.getCompareClicked();
      const hasFilterData = currentFilterData &&
        currentFilterData.selectedEntities &&
        currentFilterData.selectedEntities.length > 0 &&
        currentFilterData.category &&
        currentFilterData.country;

      if (forceReload || (compareClicked && hasFilterData) || (forceReload && hasFilterData)) {
        // Use direct service call like AI Visibility Index
        this.loadCategoryAnalysisData(forceReload);
        return;
      } else {
        // Load sample data
        this.handleEducationTabLoadWithSampleData(tabId);
        return;
      }
    }

    // Handle Sentiment Tracking tab specifically - now self-contained
    if (tabId === 'sentiment-tracking') {
      // The sentiment-analysis-tab component now handles its own data loading
      // Just load sample data for backward compatibility
      this.handleEducationTabLoadWithSampleData(tabId);
      return;
    }

    // Handle Area Comparison tab specifically
    if (tabId === 'area-comparison') {
      const cachedData = this.tabDataCache['area-comparison'];
      if (!forceReload && cachedData?.universities) {
        // // console.log('Area Comparison data found in cache, loading from cache');
        this.updateTabData(cachedData, 'area-comparison');
        this.isTabLoading = false;
        return;
      }
      // Only load API data if we have filter data and compare is clicked or force reload
      const currentFilterData = this.templateCommonService.getCurrentFilterData();
      const compareClicked = this.templateCommonService.getCompareClicked();
      const hasFilterData = currentFilterData &&
        currentFilterData.selectedEntities &&
        currentFilterData.selectedEntities.length > 0 &&
        currentFilterData.category &&
        currentFilterData.country;

      if (forceReload || (compareClicked && hasFilterData) || (forceReload && hasFilterData)) {
        this.loadAreaComparisonData(forceReload);
      } else {
        // Load sample data
        this.handleEducationTabLoadWithSampleData(tabId);
      }
      return;
    }

    // Handle AI Content Index tab specifically
    if (tabId === 'ai-content-index') {
      const currentFilterData = this.templateCommonService.getCurrentFilterData();
      const compareClicked = this.templateCommonService.getCompareClicked();
      const hasFilterData = currentFilterData &&
        currentFilterData.selectedEntities &&
        currentFilterData.selectedEntities.length > 0 &&
        currentFilterData.category &&
        currentFilterData.country;

      if (forceReload || (compareClicked && hasFilterData) || (forceReload && hasFilterData)) {
        this.loadAIContentIndexData(forceReload);
      } else {
        // Load sample data
        this.handleEducationTabLoadWithSampleData(tabId);
      }
      return;
    }

    // Handle Content Creation tab specifically
    if (tabId === 'content-creation') {
      const currentFilterData = this.templateCommonService.getCurrentFilterData();
      const compareClicked = this.templateCommonService.getCompareClicked();
      const hasFilterData = currentFilterData &&
        currentFilterData.selectedEntities &&
        currentFilterData.selectedEntities.length > 0 &&
        currentFilterData.category &&
        currentFilterData.country;

      if (forceReload || (compareClicked && hasFilterData) || (forceReload && hasFilterData)) {
        this.loadContentCreationData(forceReload);
      } else {
        // Load sample data
        this.handleEducationTabLoadWithSampleData(tabId);
      }
      return;
    }

    // Handle specific education tabs that need their own API calls
    if (['area-comparison', 'student-comparison'].includes(tabId)) {
      const currentFilterData = this.templateCommonService.getCurrentFilterData();
      const compareClicked = this.templateCommonService.getCompareClicked();
      const hasFilterData = currentFilterData &&
        currentFilterData.selectedEntities &&
        currentFilterData.selectedEntities.length > 0 &&
        currentFilterData.category &&
        currentFilterData.country;

      if ((compareClicked && hasFilterData) || (forceReload && hasFilterData)) {
        if (currentFilterData) {
          this.loadEducationTabData(tabId, currentFilterData);
          return; // Don't set loading to false here as the service will handle it
        }
      } else {
        // Load sample data
        this.handleEducationTabLoadWithSampleData(tabId);
        return;
      }
    }

    // Handle enhanced tabs with dedicated API endpoints
    if (['methodology', 'performance-tracking', 'consumer-insights'].includes(tabId)) {
      const currentFilterData = this.templateCommonService.getCurrentFilterData();
      const compareClicked = this.templateCommonService.getCompareClicked();
      const hasFilterData = currentFilterData &&
        currentFilterData.selectedEntities &&
        currentFilterData.selectedEntities.length > 0 &&
        currentFilterData.category &&
        currentFilterData.country;
      // // console.log('hasFilterData', hasFilterData);
      // For enhanced tabs, always try to load API data if we have filter data, regardless of compare state
      if (forceReload || hasFilterData) {
        this.loadEnhancedTabWithDedicatedAPI(tabId, forceReload);
        return;
      } else {
        // Load sample data if no filter data
        this.isTabLoading = true;
        this.handleEducationTabLoadWithSampleData(tabId);
        return;
      }
    }

    // For overview tab, trigger main analysis if needed
    if (tabId === 'overview') {
      const currentResponse = this.templateCommonService.getCurrentAnalysisResponse();
      const compareClicked = this.templateCommonService.getCompareClicked();
      const currentFilterData = this.templateCommonService.getCurrentFilterData();
      const hasFilterData = currentFilterData &&
        currentFilterData.selectedEntities &&
        currentFilterData.selectedEntities.length > 0 &&
        currentFilterData.category &&
        currentFilterData.country;

      //   // console.log('Overview tab logic:', {
      //   hasCurrentResponse: !!currentResponse,
      //     forceReload,
      //     compareClicked,
      //     hasFilterData
      // });

      // Only trigger analysis if compare is clicked or force reload with filter data
      if ((compareClicked && hasFilterData) || (forceReload && hasFilterData)) {
        if (!currentResponse || forceReload) {
          if (currentFilterData) {
            // // console.log('Triggering analysis for overview tab');
            this.templateCommonService.triggerAnalysis(currentFilterData);
            return;
          }
        }
        // Use existing response data if available
        if (currentResponse) {
          this.processExistingResponse(tabId, currentResponse);
        } else {
          // No response available, load sample data
          this.handleEducationTabLoadWithSampleData(tabId);
        }
      } else {
        // Default behavior: load sample data
        // // console.log('Loading sample data for overview tab (default behavior)');
        this.handleEducationTabLoadWithSampleData(tabId);
      }
    }

    this.isTabLoading = false;
  }

  private handleEducationTabLoadWithSampleData(tabId: string): void {
    // // console.log('Loading tab data with sample data for:', tabId);

    // Import and use sample education data
    const sampleData = this.getSampleEducationDataForTab(tabId);
    // // console.log('Sample data loaded for', tabId, ':', sampleData);
    this.updateTabData(sampleData);
    this.isTabLoading = false;
  }

  private getSampleEducationDataForTab(tabId: string): TabData | null {
    // // console.log('Getting sample education data for tab:', tabId);
    try {
      // Dynamic import to avoid circular dependencies
      const sampleDataModule = require('../../../sample-education');
      // // console.log('Sample data module loaded:', Object.keys(sampleDataModule));

      // Type-safe access to sampleEducationData with proper checking
      if (tabId in sampleEducationData) {
        const sampleDataKey = sampleEducationData[tabId as keyof typeof sampleEducationData];
        // // console.log('Sample data key for', tabId, ':', sampleDataKey);

        if (sampleDataKey && sampleDataModule[sampleDataKey]) {
          const data = sampleDataModule[sampleDataKey];
          // // console.log('Sample data found for', tabId, ':', data);
          return data;
        } else {
          console.warn('Sample data not found for key:', sampleDataKey);
        }
      } else {
        console.warn('Tab ID not found in sampleEducationData:', tabId);
      }
    } catch (error) {
      console.warn('Could not load sample data for tab:', tabId, error);
    }

    // Fallback to basic sample data
    // // console.log('Falling back to basic sample data for tab:', tabId);
    return this.getSampleDataForTab(tabId);
  }

  private processExistingResponse(tabId: string, response: any): void {
    if (!response) {
      // No response available, load sample data
      this.handleEducationTabLoadWithSampleData(tabId);
      return;
    }

    switch (tabId) {
      case 'overview':
        const overviewData = {
          table: response.table || [],
          chart: response.chart || null,
          evidence_sources: response.evidence_sources || [],
          recommendations: [],
          citations: response.evidence_sources || [],
          source_chips: [],
          citation_badge: null
        };
        this.updateTabData(overviewData);
        break;
      default:
        // For other tabs, use sample data as fallback
        const sampleData = this.getSampleEducationDataForTab(tabId);
        this.updateTabData(sampleData);
        break;
    }
  }

  private loadEducationTabData(tabId: string, filterData: any): void {
    // // console.log('CommonTabsComponent: Loading education tab data for:', tabId);

    this.isTabLoading = true;
    this.templateCommonService.loadEducationTabData(tabId, filterData).subscribe({
      next: (response) => {
        // // console.log('Education tab data loaded successfully:', response);

        // Transform response to match expected format
        const transformedData = {
          table: response.table || [],
          chart: response.chart || null,
          evidence_sources: response.evidence_sources || [],
          recommendations: response.recommendations || [],
          citations: response.citations || [],
          source_chips: response.source_chips || [],
          citation_badge: response.citation_badge || null
        };

        this.updateTabData(transformedData, tabId);
        this.isTabLoading = false;
      },
      error: (error) => {
        console.error('Error loading education tab data:', error);
        this.error = `Failed to load ${tabId} data`;

        // Fallback to sample data on error
        const sampleData = this.getSampleEducationDataForTab(tabId);
        this.updateTabData(sampleData, tabId);
        this.isTabLoading = false;
      }
    });
  }

  // NEW: Load enhanced tabs with dedicated API endpoints (following AI Visibility Index pattern)
  private loadEnhancedTabWithDedicatedAPI(tabId: string, forceReload: boolean = false): void {
    // // console.log('CommonTabsComponent: Loading enhanced tab with dedicated API for:', tabId);

    this.isTabLoading = true;
    this.error = null;

    let currentFilterData = this.templateCommonService.getCurrentFilterData();
    if (!currentFilterData || !currentFilterData.selectedEntities || currentFilterData.selectedEntities.length === 0) {
      if (forceReload) {
        currentFilterData = {
          selectedEntities: this.selectedEntities,
          category: this.category,
          country: this.country
        };
      } else {
        this.error = 'No entities selected for analysis';
        this.isTabLoading = false;
        return;
      }
    }

    let apiCall: Observable<any>;

    switch (tabId) {
      case 'methodology':
        apiCall = this.templateCommonService.loadOurMethodologyAnalysis(
          currentFilterData.selectedEntities,
          currentFilterData.category,
          forceReload
        );
        break;
      case 'performance-tracking':
        apiCall = this.templateCommonService.loadPerformanceTrackingAnalysis(
          currentFilterData.selectedEntities,
          currentFilterData.category,
          forceReload
        );
        break;
      case 'consumer-insights':
        apiCall = this.templateCommonService.loadConsumerInsightsAnalysis(
          currentFilterData.selectedEntities,
          currentFilterData.category,
          forceReload
        );
        break;
      default:
        this.error = `Unknown enhanced tab: ${tabId}`;
        this.isTabLoading = false;
        return;
    }

    apiCall.subscribe({
      next: (response) => {
        // // console.log(`Enhanced ${tabId} data loaded successfully:`, response);
        this.updateTabData(response, tabId);
        this.isTabLoading = false;
      },
      error: (error) => {
        console.error(`Error loading enhanced ${tabId} data:`, error);
        this.error = `Failed to load ${tabId} data`;

        // Fallback to sample data on error
        const sampleData = this.getSampleEducationDataForTab(tabId);
        this.updateTabData(sampleData, tabId);
        this.isTabLoading = false;
      }
    });
  }



  private loadAIVisibilityIndexData(forceRefresh: boolean = false): void {
    // // console.log('Loading AI Visibility Index data for entities:', this.selectedEntities, 'forceRefresh:', forceRefresh);
    // // console.log('Setting isTabLoading to true');

    this.isTabLoading = true;
    this.templateCommonService.loadAIVisibilityIndex(this.selectedEntities, this.category, forceRefresh).subscribe({
      next: (transformedData) => {
        // // console.log('AI Visibility Index data loaded successfully:', transformedData);
        // // console.log('ai_visibility_data in transformedData:', transformedData?.ai_visibility_data);
        this.updateTabData(transformedData, 'ai-visibility-index');
        // // console.log('After updateTabData - this.tabData?.ai_visibility_data:', this.tabData?.ai_visibility_data);
        // // console.log('Setting isTabLoading to false (success)');
        this.isTabLoading = false;

        // Check data after a short delay to ensure it's properly set
        setTimeout(() => {
          // // console.log('Delayed check - this.tabData?.ai_visibility_data:', this.tabData?.ai_visibility_data);
        }, 100);
      },
      error: (error) => {
        console.error('Error loading AI Visibility Index data:', error);
        this.error = 'Failed to load AI Visibility Index data';
        this.updateTabData({
          table: [],
          chart: null,
          evidence_sources: [],
          recommendations: [],
          citations: [],
          source_chips: [],
          citation_badge: null
        }, 'ai-visibility-index');
        // // console.log('Setting isTabLoading to false (error)');
        this.isTabLoading = false;
      }
    });
  }

  private loadCategoryAnalysisData(forceRefresh: boolean = false): void {
    // // console.log('Loading Category Analysis data for entities:', this.selectedEntities, 'forceRefresh:', forceRefresh);

    this.isTabLoading = true;
    this.templateCommonService.loadCategoryAnalysisIndex(this.selectedEntities, this.category, forceRefresh).subscribe({
      next: (transformedData) => {
        // // console.log('Category Analysis data loaded successfully:', transformedData);
        this.updateTabData(transformedData, 'category-analysis');
        this.isTabLoading = false;
      },
      error: (error) => {
        console.error('Error loading Category Analysis data:', error);
        this.error = 'Failed to load Category Analysis data';
        this.updateTabData({
          table: [],
          chart: null,
          evidence_sources: [],
          recommendations: [],
          citations: [],
          source_chips: [],
          citation_badge: null
        }, 'category-analysis');
        this.isTabLoading = false;
      }
    });
  }


  private loadAIContentIndexData(forceRefresh: boolean = false): void {
    // // console.log('Loading AI Content Index data for entities:', this.selectedEntities, 'forceRefresh:', forceRefresh);

    this.isTabLoading = true;
    this.templateCommonService.loadAIContentIndex(this.selectedEntities, this.category, forceRefresh).subscribe({
      next: (transformedData) => {
        // // console.log('AI Content Index data loaded successfully:', transformedData);
        this.updateTabData(transformedData, 'ai-content-index');
        this.isTabLoading = false;
      },
      error: (error) => {
        console.error('Error loading AI Content Index data:', error);
        this.error = 'Failed to load AI Content Index data';
        this.updateTabData({
          table: [],
          chart: null,
          evidence_sources: [],
          recommendations: [],
          citations: [],
          source_chips: [],
          citation_badge: null
        }, 'ai-content-index');
        this.isTabLoading = false;
      }
    });
  }

  private loadContentCreationData(forceRefresh: boolean = false): void {
    // // console.log('Loading Content Creation data for entities:', this.selectedEntities, 'forceRefresh:', forceRefresh);

    this.isTabLoading = true;
    this.templateCommonService.loadContentCreation(this.selectedEntities, this.category, forceRefresh).subscribe({
      next: (transformedData: any) => {
        // // console.log('Content Creation data loaded successfully:', transformedData);
        this.updateTabData(transformedData, 'content-creation');
        this.isTabLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading Content Creation data:', error);
        this.error = 'Failed to load Content Creation data';
        this.updateTabData({
          table: [],
          chart: null,
          evidence_sources: [],
          recommendations: [],
          citations: [],
          source_chips: [],
          citation_badge: null
        }, 'content-creation');
        this.isTabLoading = false;
      }
    });
  }

  private loadAreaComparisonData(forceRefresh: boolean = false): void {
    // // console.log('Loading Area Comparison data for entities:', this.selectedEntities, 'forceRefresh:', forceRefresh);

    this.isTabLoading = true;
    this.templateCommonService.loadAreaComparisonIndex(this.selectedEntities, this.category, forceRefresh).subscribe({
      next: (transformedData) => {
        // // console.log('Area Comparison data loaded successfully:', transformedData);
        this.updateTabData(transformedData, 'area-comparison');
        this.isTabLoading = false;
      },
      error: (error) => {
        console.error('Error loading Area Comparison data:', error);
        this.error = 'Failed to load Area Comparison data';
        this.updateTabData({
          table: [],
          chart: null,
          evidence_sources: [],
          recommendations: [],
          citations: [],
          source_chips: [],
          citation_badge: null
        }, 'area-comparison');
        this.isTabLoading = false;
      }
    });
  }



  private updateTabData(data: TabData | null | any, tabId?: string): void {
    // // console.log('updateTabData called with:', data, 'for tab:', tabId || this.activeTab);
    // // console.log('ai_visibility_data in incoming data:', data?.ai_visibility_data);

    this.tabData = data;
    this.dataSubject.next(data);

    // Cache the data for the specific tab
    if ((tabId || this.activeTab) && (tabId || this.activeTab) !== null) {
      const targetTabId = tabId || this.activeTab;
      this.tabDataCache[targetTabId!] = data;
      // // console.log('Cached data for tab:', targetTabId, 'Data:', this.tabDataCache[targetTabId!]);
    }

    // Update enhanced data if available
    if (data) {
      this.evidenceSources = data.evidence_sources || [];
      this.recommendations = data.recommendations || [];
      this.citations = data.citations || [];
      this.sourceChips = data.source_chips || [];
      this.citationBadge = data.citation_badge || null;

      this.evidenceSubject.next(this.evidenceSources);
      this.recommendationsSubject.next(this.recommendations);
    }

    // // console.log('After setting this.tabData:', this.tabData);
    // // console.log('ai_visibility_data in this.tabData:', this.tabData?.ai_visibility_data);
  }

  private getSampleDataForTab(tabId: string): TabData | null {
    // Return sample data based on tab ID
    // This will be replaced with actual API calls
    const sampleData: { [key: string]: TabData } = {
      'overview': {
        table: [],
        chart: null,
        evidence_sources: [],
        recommendations: [],
        citations: [],
        source_chips: [],
        citation_badge: null
      },
      'ai-visibility-index': {
        table: [],
        chart: null,
        evidence_sources: [],
        recommendations: [],
        citations: [],
        source_chips: [],
        citation_badge: null
      },
      'sentiment-tracking': {
        table: [],
        chart: null,
        evidence_sources: [],
        recommendations: [],
        citations: [],
        source_chips: [],
        citation_badge: null
      },
      'category-analysis': {
        table: [],
        chart: null,
        evidence_sources: [],
        recommendations: [],
        citations: [],
        source_chips: [],
        citation_badge: null
      },
      'area-comparison': {
        table: [],
        chart: null,
        evidence_sources: [],
        recommendations: [],
        citations: [],
        source_chips: [],
        citation_badge: null
      }
    };

    return sampleData[tabId] || null;
  }

  // Helper methods for data transformation
  transformBrandAnalysis(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  }

  transformSentimentAnalysis(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  }

  transformConsumerPreference(data: any): any[] {
    // Handle the new Category Analysis API response structure
    if (data && data.category_analysis) {
      // Transform category analysis data to the format expected by the component
      const transformedData: any[] = [];

      data.category_analysis.forEach((category: any) => {
        category.universities.forEach((university: any) => {
          transformedData.push({
            brand: university.name, // Component expects 'brand' field
            category: category.category,
            score: university.score,
            world_ranking: university.rankings?.world_ranking || 'N/A',
            category_ranking: university.rankings?.category_ranking || 'N/A',
            employment_rate: university.employment_data?.employment_rate || 'N/A',
            average_salary: university.employment_data?.average_salary || 'N/A',
            domestic_fees: university.fees?.domestic || 'N/A',
            international_fees: university.fees?.international || 'N/A',
            course_offerings: university.course_offerings?.join(', ') || 'N/A',
            strengths: university.strengths?.join(', ') || 'N/A',
            weaknesses: university.weaknesses?.join(', ') || 'N/A'
          });
        });
      });

      return transformedData;
    }

    // Handle legacy data format
    if (Array.isArray(data)) {
      return data;
    }

    return [];
  }

  transformAreaComparison(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  }

  transformContentDistribution(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  }

  transformMethodology(data: any): any {
    if (data && data.methodology_data) {
      return data.methodology_data;
    }
    return data || {};
  }

  // NEW: Transformer methods for enhanced tabs
  transformPerformanceTracking(data: any): any {
    if (data && data.performance_tracking_data) {
      return data.performance_tracking_data;
    }
    return data || {};
  }


  transformConsumerInsights(data: any): any {
    if (data && data.consumer_insights_data) {
      return data.consumer_insights_data;
    }
    return data || {};
  }

  // Helper methods for UI
  getTabTitle(tabId: string): string {
    const tab = this.config.tabs.find(t => t.id === tabId);
    return tab ? tab.name : tabId;
  }

  getActiveTabIcon(): string {
    if (!this.activeTab) return 'dashboard';
    const tab = this.config.tabs.find(t => t.id === this.activeTab);
    return tab ? tab.icon : 'dashboard';
  }

  getEntityNames(): string[] {
    return this.selectedEntities.map(entity => entity.name || entity);
  }

  getEntityText(): string {
    if (!this.selectedEntities || this.selectedEntities.length === 0) {
      return this.config.entityPlural;
    }

    const entityNames = this.getEntityNames();

    if (this.selectedEntities.length === 1) {
      return entityNames[0];
    }

    if (this.selectedEntities.length === 2) {
      return `${entityNames[0]} and ${entityNames[1]}`;
    }

    if (this.selectedEntities.length <= 5) {
      const last = entityNames[entityNames.length - 1];
      const others = entityNames.slice(0, -1).join(', ');
      return `${others} and ${last}`;
    }

    return `${entityNames.slice(0, 3).join(', ')} and ${this.selectedEntities.length - 3} more`;
  }

  getChartMetrics(): string[] {
    return this.config.chartMetrics || [];
  }

  getTableColumns(): TableColumn[] {
    return this.config.tableColumns || [];
  }

  getDisplayedColumns(): string[] {
    return this.config.displayedColumns || [];
  }

  getMaxEntities(): number {
    return this.config.maxEntities || 5;
  }

  // Method to update data from parent component
  updateData(data: TabData): void {
    this.updateTabData(data);
  }

  // Method to update loading state from parent component
  updateLoadingState(loading: boolean): void {
    this.isLoading = loading;
  }

  // Method to update error state from parent component
  updateErrorState(error: string | null): void {
    this.error = error;
  }

}
